import React, { Component } from "react";

/* ═══════════════════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════════════════ */

function getGrade(accuracy) {
  const a = parseFloat(accuracy);
  if (isNaN(a)) return { grade: "—",  color: "#9ca3af", bg: "#f3f4f6" };
  if (a >= 90)  return { grade: "A+", color: "#15803d", bg: "#dcfce7" };
  if (a >= 80)  return { grade: "A",  color: "#1d4ed8", bg: "#dbeafe" };
  if (a >= 70)  return { grade: "B+", color: "#6d28d9", bg: "#ede9fe" };
  if (a >= 60)  return { grade: "B",  color: "#b45309", bg: "#fef3c7" };
  if (a >= 50)  return { grade: "C",  color: "#c2410c", bg: "#ffedd5" };
  return         { grade: "D",  color: "#dc2626", bg: "#fee2e2" };
}

/** All unique exams sorted by id */
function getAllExams(data) {
  const map = new Map();
  (data || []).forEach(s => {
    (s.examWiseReport || []).forEach(e => {
      if (!map.has(e.offline_exam_paper_id)) {
        map.set(e.offline_exam_paper_id, e.paper_name || String(e.offline_exam_paper_id));
      }
    });
  });
  return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([id, name]) => ({ id, name }));
}

/** Unique subjects for a given exam id, sorted by subject_id */
function getSubjectsForExam(data, examId) {
  const map = new Map();
  (data || []).forEach(s => {
    const exam = (s.examWiseReport || []).find(e => e.offline_exam_paper_id === examId);
    if (!exam) return;
    (exam.subject_marks_rank || []).forEach(sub => {
      if (!map.has(sub.subject_name)) map.set(sub.subject_name, Number(sub.subject_id) || 99);
    });
  });
  return [...map.entries()].sort((a, b) => a[1] - b[1]).map(([name]) => name);
}

/** Get exam data for a student */
function getExam(student, examId) {
  return (student.examWiseReport || []).find(e => e.offline_exam_paper_id === examId) || null;
}

/** Flatten all students with all exams for sorting/filtering */
function flattenAll(data, allExams) {
  return (data || []).map(s => {
    const row = {
      _raw:         s,
      roll_no:      String(s.roll_no      ?? ""),
      student_name: String(s.student_name ?? "-"),
      branch_name:  String(s.branch_name  ?? "-"),
      section_name: String(s.section_name ?? "-"),
    };
    allExams.forEach(exam => {
      const e = getExam(s, exam.id);
      row[`exam_${exam.id}_total`]    = e?.Total_Marks ?? "-";
      row[`exam_${exam.id}_accuracy`] = e?.Accuracy    ?? "-";
      row[`exam_${exam.id}_rank`]     = e?.Exam_Rank   ?? "-";
      (e?.subject_marks_rank || []).forEach(sub => {
        const key = sub.subject_name.toLowerCase().replace(/\s+/g, "_");
        row[`exam_${exam.id}_${key}_marks`] = sub.subject_marks ?? "-";
        row[`exam_${exam.id}_${key}_rank`]  = sub.subject_rank  ?? "-";
      });
    });
    return row;
  });
}

/* ═══════════════════════════════════════════════════════════════════════
   PDF
═══════════════════════════════════════════════════════════════════════ */
async function generatePDFViaServer(selectedStudents, allExams) {
  try {
    const res = await fetch("https://rizee-college-backend-dev1-755214126997.us-south1.run.app/generate-report-card", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ students: selectedStudents, allExams }),
    });
    if (!res.ok) throw new Error("Server error " + res.status);
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `Report_Cards_${Date.now()}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("PDF error:", err);
    alert("PDF generation failed: " + err.message);
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   MULTI-EXAM TABLE (class component — no hooks)
   Uses plain HTML table with colspan/rowspan for grouped headers
═══════════════════════════════════════════════════════════════════════ */
class MultiExamTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sortKey:   null,   // column key string
      sortAsc:   true,
      search:    "",
      page:      0,
      pageSize:  10,
    };
    this.handleSort   = this.handleSort.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
  }

  handleSort(key) {
    this.setState(prev => ({
      sortKey: key,
      sortAsc: prev.sortKey === key ? !prev.sortAsc : true,
      page:    0,
    }));
  }

  handleSearch(val) {
    this.setState({ search: val, page: 0 });
  }

  getSortedFiltered() {
    const { flatRows } = this.props;
    const { sortKey, sortAsc, search } = this.state;

    let rows = flatRows;

    // Filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        r.student_name.toLowerCase().includes(q) ||
        r.roll_no.toLowerCase().includes(q)      ||
        r.branch_name.toLowerCase().includes(q)  ||
        r.section_name.toLowerCase().includes(q)
      );
    }

    // Sort
    if (sortKey) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortKey] ?? "-";
        const bv = b[sortKey] ?? "-";
        const an = parseFloat(av);
        const bn = parseFloat(bv);
        let cmp;
        if (!isNaN(an) && !isNaN(bn)) {
          cmp = an - bn;
        } else {
          cmp = String(av).localeCompare(String(bv));
        }
        return sortAsc ? cmp : -cmp;
      });
    }

    return rows;
  }

  render() {
    const {
      allExams, examSubjects, selectedIds, onToggle, onToggleAll,
    } = this.props;
    const { page, pageSize, search, sortKey, sortAsc } = this.state;

    const filtered   = this.getSortedFiltered();
    const totalPages = Math.ceil(filtered.length / pageSize) || 1;
    const pageRows   = filtered.slice(page * pageSize, (page + 1) * pageSize);
    const allChecked = selectedIds.size === filtered.length && filtered.length > 0;

    // Th helper with sort indicator
    const Th = ({ label, sortK, style = {}, rowSpan, colSpan }) => {
      const active = sortKey === sortK;
      return (
        <th
          rowSpan={rowSpan} colSpan={colSpan}
          onClick={() => sortK && this.handleSort(sortK)}
          style={{
            background:    "#0f172a",
            color:         "#e2e8f0",
            padding:       "9px 10px",
            textAlign:     "center",
            whiteSpace:    "nowrap",
            fontWeight:    600,
            fontSize:      11,
            letterSpacing: "0.3px",
            textTransform: "uppercase",
            borderRight:   "1px solid #1e293b",
            borderBottom:  "1px solid #1e293b",
            cursor:        sortK ? "pointer" : "default",
            userSelect:    "none",
            ...style,
          }}
        >
          {label}
          {sortK && (active ? (sortAsc ? " ▲" : " ▼") : " ⇅")}
        </th>
      );
    };

    /* ── How many sub-columns per exam ── */
    const examCols = allExams.map(exam => {
      const subs = examSubjects[exam.id] || [];
      // 3 summary cols (Total, Accuracy, Rank) + 2 per subject (Marks, Rank)
      return { exam, subs, count: 3 + subs.length * 2 };
    });

    return (
      <div>
        {/* ── Search bar ── */}
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
          <input
            type="text" value={search}
            onChange={e => this.handleSearch(e.target.value)}
            placeholder="🔍  Search name, roll no, branch..."
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, outline: "none", width: 280 }}
          />
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {filtered.length} of {this.props.flatRows.length} students
          </span>
        </div>

        {/* ── Table ── */}
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #e2e8f0" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
            <thead>

              {/* ── Row 1: fixed columns + exam group headers ── */}
              <tr>
                {/* Checkbox header — spans 2 rows */}
                <th rowSpan={2} style={{
                  background: "#0f172a", padding: "9px 10px", border: "1px solid #1e293b",
                  textAlign: "center", verticalAlign: "middle",
                }}>
                  <input type="checkbox" checked={allChecked} onChange={onToggleAll}
                    style={{ cursor: "pointer", width: 14, height: 14 }} />
                </th>

                {/* Student / Roll / Branch / Section — span 2 rows */}
                {[
                  { label: "Student",  k: "student_name" },
                  { label: "Roll No",  k: "roll_no"       },
                  { label: "Branch",   k: "branch_name"   },
                  { label: "Section",  k: "section_name"  },
                ].map(c => (
                  <Th key={c.k} label={c.label} sortK={c.k} rowSpan={2}
                    style={{ verticalAlign: "middle" }} />
                ))}

                {/* Exam group headers */}
                {examCols.map(({ exam, count }) => (
                  <th key={exam.id} colSpan={count} style={{
                    background:    "#1e3a5f",
                    color:         "#93c5fd",
                    padding:       "8px 10px",
                    textAlign:     "center",
                    fontWeight:    700,
                    fontSize:      11,
                    letterSpacing: "0.3px",
                    border:        "1px solid #1e293b",
                    whiteSpace:    "nowrap",
                  }}>
                    📋 {exam.name}
                  </th>
                ))}
              </tr>

              {/* ── Row 2: sub-column headers per exam ── */}
              <tr>
                {examCols.map(({ exam, subs }) => (
                  <React.Fragment key={exam.id}>
                    <Th label="Total"    sortK={`exam_${exam.id}_total`}    />
                    <Th label="Accuracy" sortK={`exam_${exam.id}_accuracy`} />
                    <Th label="Rank"     sortK={`exam_${exam.id}_rank`}     />
                    {subs.map(sub => {
                      const key = sub.toLowerCase().replace(/\s+/g, "_");
                      return (
                        <React.Fragment key={sub}>
                          <Th label={`${sub} Marks`} sortK={`exam_${exam.id}_${key}_marks`} />
                          <Th label={`${sub} Rank`}  sortK={`exam_${exam.id}_${key}_rank`}  />
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tr>
            </thead>

            <tbody>
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={999} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>
                    No records found.
                  </td>
                </tr>
              )}
              {pageRows.map((row, i) => {
                const isSelected = selectedIds.has(row.roll_no);
                const bg = isSelected ? "#dbeafe" : i % 2 === 0 ? "#fff" : "#f8fafc";
                return (
                  <tr key={row.roll_no} onClick={() => onToggle(row.roll_no)}
                    style={{ background: bg, cursor: "pointer" }}>

                    {/* Checkbox */}
                    <td style={{ textAlign: "center", padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}
                      onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected}
                        onChange={() => onToggle(row.roll_no)}
                        style={{ cursor: "pointer", width: 14, height: 14 }} />
                    </td>

                    {/* Fixed cols */}
                    <td style={{ padding: "8px 10px", fontWeight: 700, whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9", color: "#0f172a" }}>{row.student_name}</td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9", color: "#475569" }}>{row.roll_no}</td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9", color: "#475569" }}>{row.branch_name}</td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9", color: "#475569" }}>{row.section_name}</td>

                    {/* Per-exam data */}
                    {examCols.map(({ exam, subs }) => {
                      const total    = row[`exam_${exam.id}_total`]    || "-";
                      const accuracy = row[`exam_${exam.id}_accuracy`] || "-";
                      const rank     = row[`exam_${exam.id}_rank`]     || "-";
                      const absent   = total === "-";
                      const g        = getGrade(accuracy);
                      const td = (val, extra = {}) => (
                        <td style={{
                          padding: "8px 10px", textAlign: "center",
                          whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9",
                          borderLeft: "1px solid #e2e8f0",
                          color: absent ? "#9ca3af" : "#1e293b",
                          ...extra,
                        }}>
                          {val}
                        </td>
                      );
                      return (
                        <React.Fragment key={exam.id}>
                          {/* Total */}
                          {td(absent ? "-" : <b style={{ color: "#0f172a" }}>{total}</b>)}

                          {/* Accuracy with grade badge */}
                          {td(absent ? "-" : (
                            <span style={{
                              background: g.bg, color: g.color,
                              padding: "1px 7px", borderRadius: 10,
                              fontWeight: 700, fontSize: 11,
                            }}>
                              {accuracy}%
                            </span>
                          ))}

                          {/* Rank */}
                          {td(absent ? "-" : (
                            <span style={{ fontWeight: 700, color: "#1d4ed8" }}>#{rank}</span>
                          ))}

                          {/* Subjects */}
                          {subs.map(sub => {
                            const key   = sub.toLowerCase().replace(/\s+/g, "_");
                            const marks = row[`exam_${exam.id}_${key}_marks`] || "-";
                            const srank = row[`exam_${exam.id}_${key}_rank`]  || "-";
                            const isAbs = marks === "-";
                            return (
                              <React.Fragment key={sub}>
                                {td(isAbs ? "-" : <span style={{ fontWeight: 600 }}>{marks}</span>)}
                                {td(isAbs ? "-" : <span style={{ color: "#0891b2", fontWeight: 600 }}>#{srank}</span>)}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, color: "#475569" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => this.setState(p => ({ page: Math.max(0, p.page - 1) }))}
              disabled={page === 0}
              style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #cbd5e1", background: page === 0 ? "#f1f5f9" : "#fff", cursor: page === 0 ? "default" : "pointer", fontSize: 13 }}>
              ← Prev
            </button>
            <span>Page <b>{page + 1}</b> of <b>{totalPages}</b></span>
            <button onClick={() => this.setState(p => ({ page: Math.min(totalPages - 1, p.page + 1) }))}
              disabled={page >= totalPages - 1}
              style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #cbd5e1", background: page >= totalPages - 1 ? "#f1f5f9" : "#fff", cursor: page >= totalPages - 1 ? "default" : "pointer", fontSize: 13 }}>
              Next →
            </button>
            <span style={{ marginLeft: 4 }}>| Go to:</span>
            <input type="number" min={1} max={totalPages}
              defaultValue={page + 1}
              onChange={e => {
                const p = Number(e.target.value) - 1;
                if (p >= 0 && p < totalPages) this.setState({ page: p });
              }}
              style={{ width: 44, padding: "3px 6px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13 }}
            />
          </div>
          <select value={pageSize}
            onChange={e => this.setState({ pageSize: Number(e.target.value), page: 0 })}
            style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13 }}>
            {[10, 20, 50, this.props.flatRows.length].map(n => (
              <option key={n} value={n}>{n === this.props.flatRows.length ? "Show All" : `Show ${n}`}</option>
            ))}
          </select>
        </div>
      </div>
    );
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   MAIN CLASS: OfflineexamresultsNew
═══════════════════════════════════════════════════════════════════════ */
class OfflineexamresultsNew extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedIds: new Set(),
      generating:  false,
    };
    this.handleToggle      = this.handleToggle.bind(this);
    this.handleToggleAll   = this.handleToggleAll.bind(this);
    this.handleSelectAll   = this.handleSelectAll.bind(this);
    this.handleClear       = this.handleClear.bind(this);
    this.handleGeneratePDF = this.handleGeneratePDF.bind(this);
    // Cache for stable reference comparison
    this._prevDataKey = null;
  }

  getData() {
    return this.props.getOffExamResults || this.props.getNewOffExamResults || [];
  }

  componentDidUpdate() {
    const data    = this.getData();
    const dataKey = data.length + "_" + (data[0]?.roll_no ?? "");
    if (this._prevDataKey !== dataKey) {
      this._prevDataKey = dataKey;
      // Only clear selection when data set actually changes
      this.setState({ selectedIds: new Set() });
    }
  }

  handleToggle(rollNo) {
    this.setState(prev => {
      const next = new Set(prev.selectedIds);
      next.has(rollNo) ? next.delete(rollNo) : next.add(rollNo);
      return { selectedIds: next };
    });
  }

  handleToggleAll() {
    const data = this.getData();
    const allIds = data.map(s => String(s.roll_no));
    this.setState(prev => ({
      selectedIds: prev.selectedIds.size === allIds.length ? new Set() : new Set(allIds),
    }));
  }

  handleSelectAll() {
    this.setState({ selectedIds: new Set(this.getData().map(s => String(s.roll_no))) });
  }

  handleClear() {
    this.setState({ selectedIds: new Set() });
  }

  async handleGeneratePDF() {
    const { selectedIds } = this.state;
    const data     = this.getData();
    const allExams = getAllExams(data);
    const selected = data.filter(s => selectedIds.has(String(s.roll_no)));
    if (!selected.length) { alert("Please select at least one student."); return; }
    this.setState({ generating: true });
    try {
      await generatePDFViaServer(selected, allExams);
    } finally {
      this.setState({ generating: false });
    }
  }

  render() {
    const rawData                     = this.getData();
    const { selectedIds, generating } = this.state;
    const hasSelection                = selectedIds.size > 0;

    if (!rawData.length) {
      return (
        <div style={{ padding: 48, textAlign: "center", color: "#64748b", fontSize: 14 }}>
          No exam results found.
        </div>
      );
    }

    const allExams    = getAllExams(rawData);
    const examSubjects = {};
    allExams.forEach(exam => {
      examSubjects[exam.id] = getSubjectsForExam(rawData, exam.id);
    });
    const flatRows = flattenAll(rawData, allExams);

    return (
      <div style={{ minHeight: "100vh", background: "#f1f5f9", padding: "20px", fontFamily: "'Segoe UI', Arial, sans-serif" }}>

        {/* ── Action bar ── */}
        <div style={{ background: "#fff", borderRadius: 12, padding: "14px 20px", marginBottom: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <h1 style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", margin: 0 }}>Offline Exam Results</h1>
              <p style={{ fontSize: 11, color: "#64748b", margin: "2px 0 0" }}>
                {allExams.length} exam{allExams.length > 1 ? "s" : ""} &bull; {rawData.length} students
                {allExams.length > 1 && (
                  <span style={{ marginLeft: 8, color: "#1d4ed8", fontWeight: 600 }}>
                    All exams shown side by side →
                  </span>
                )}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {hasSelection && (
                <span style={{ background: "#dbeafe", border: "1px solid #bfdbfe", color: "#1d4ed8", padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                  {selectedIds.size} selected
                </span>
              )}
              <button onClick={this.handleGeneratePDF} disabled={generating || !hasSelection}
                style={{
                  background: !hasSelection ? "#e2e8f0" : "linear-gradient(135deg,#1d4ed8,#0891b2)",
                  color: !hasSelection ? "#94a3b8" : "#fff", border: "none",
                  padding: "8px 18px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                  cursor: !hasSelection ? "not-allowed" : "pointer",
                  boxShadow: hasSelection ? "0 3px 10px rgba(29,78,216,0.25)" : "none",
                }}>
                {generating ? "⏳ Generating..." : "📄 Generate Report Card PDF"}
              </button>
              <button onClick={this.handleSelectAll}
                style={{ background: "#fff", border: "1px solid #cbd5e1", color: "#475569", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                ☑ Select All
              </button>
              {hasSelection && (
                <button onClick={this.handleClear}
                  style={{ background: "#fff", border: "1px solid #fecaca", color: "#dc2626", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  ✕ Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Tip ── */}
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 7, padding: "6px 12px", marginBottom: 12, fontSize: 11, color: "#166534" }}>
          💡 Click any row or checkbox to select. Click column headers to <b>sort</b>.
          All exams are shown <b>side by side</b> in the same table.
          Then click <b>Generate Report Card PDF</b>.
        </div>

        {/* ── Table ── */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 1px 12px rgba(0,0,0,0.06)", border: "1px solid #e2e8f0" }}>
          <MultiExamTable
            flatRows={flatRows}
            allExams={allExams}
            examSubjects={examSubjects}
            selectedIds={selectedIds}
            onToggle={this.handleToggle}
            onToggleAll={this.handleToggleAll}
          />
        </div>

      </div>
    );
  }
}

export default OfflineexamresultsNew;